import { useApiUtils } from '@core/util/apiUtils';
import { excluirAlert } from 'app/services/alerts';
import { bancoApi } from 'app/services/api/bancoApi';
import { contaApi } from 'app/services/api/contaApi';
import * as Actions from 'app/store/actions';
import { eventReactGA, initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { ContaTable } from './components';
import {
  EditarContaBancariaModal,
  EditarContaCaixinhaModal,
  EditarContaCartaoModal,
  VisualizarContaBancariaModal,
  VisualizarContaCaixinhaModal,
  VisualizarContaCartaoModal,
} from './components/modal';
import { ContaContext, ContaProvider } from './ContaContext';

const CONTA = 'Conta';

const Conta = () => {
  const [, setContas] = useContext(ContaContext).contas;
  const [, setSelecao] = useContext(ContaContext).selecao;
  const [, setTipoSelecao] = useContext(ContaContext).tipoSelecao;
  const [, setContaAtual] = useContext(ContaContext).contaAtual;
  const [, setBancos] = useContext(ContaContext).bancos;
  const [, setLoading] = useContext(ContaContext).loading;
  const [, setContasCorrente] = useContext(ContaContext).contasCorrente;
  const [, setTotalElements] = useContext(ContaContext).totalElements;
  const [, setHistorico] = useContext(ContaContext).historico;
  const [page, setPage] = useContext(ContaContext).page;
  const [size, setSize] = useContext(ContaContext).size;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const history = useHistory();

  useEffect(() => {
    initializeReactGA(`${history?.location?.pathname}/conta`);

    const fetch = async () => {
      await fetchBancos();
    };

    fetch();
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchContasCorrente();
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const initialPage = {
    page,
    q: '',
    size,
  };

  const fetchContasCorrente = async () => {
    try {
      const res = await contaApi.getContasCorrente();
      setContasCorrente(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchContas = async (search = initialPage) => {
    setLoading(true);

    try {
      const response = await contaApi.get(search);

      setTotalElements(response.data.totalElements);
      setPage(response.data.number);
      setSize(response.data.size);
      setContas(response.data.content);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    setLoading(false);
  };

  const fetchBancos = async () => {
    try {
      const response = await bancoApi.get();

      setBancos(response.data.content);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
  };

  const fetchConta = async (id) => {
    try {
      const response = await contaApi.getOne(id);

      fetchHistorico(response.data);

      setContaAtual(response.data);
    } catch (err) {
      console.error(err);
      apiUtils.handleApiError(err);
    }
  };

  const fetchHistorico = async (conta) => {
    const tiposImplementados = ['CONTA_CAIXINHA', 'CONTA_CORRENTE', 'CONTA_INVESTIMENTO', 'CONTA_POUPANCA'];

    if (!tiposImplementados.includes(conta?.tipo)) {
      return;
    }
    try {
      const response = await contaApi.getHistorico(conta.id);

      setHistorico((response.data || []).reverse());
    } catch (err) {
      console.error(err);
      apiUtils.handleApiError(err);
    }
  };

  const submitConta = async (conta, pageable, message, acao) => {
    try {
      eventReactGA(CONTA, acao);
      let res;
      if (conta.id) {
        res = await contaApi.put(conta.id, conta);
      } else {
        res = await contaApi.post(conta);
      }

      showSuccess(message);

      fetchContas(pageable);
      fetchContasCorrente();

      return res.data;
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    return false;
  };

  const handleSubmitContaCaixinha = async (contaCaixinha, pageable) => {
    let message;
    let acao;

    if (contaCaixinha.id) {
      message = 'Conta caixinha editada com sucesso';
      acao = 'Atualizar conta caixinha';
    } else {
      message = 'Conta caixinha salva com sucesso';
      acao = 'Cadastrar conta caixinha';
    }

    const conta = await submitConta(contaCaixinha, pageable, message, acao);

    return conta;
  };

  const handleSubmitContaBancaria = async (contaBancaria, pageable) => {
    let message;
    let acao;

    if (contaBancaria.id) {
      message = 'Conta bancária editada com sucesso';
      acao = 'Atualizar conta bancária';
    } else {
      message = 'Conta bancária salva com sucesso';
      acao = 'Cadastrar conta bancária';
    }

    const conta = await submitConta(contaBancaria, pageable, message, acao);

    return conta;
  };

  const handleSubmitContaCartao = async (contaCartao, pageable) => {
    let message;
    let acao;

    if (contaCartao.id) {
      message = 'Cartão de crédito editado com sucesso';
      acao = 'Atualizar cartão de crédito';
    } else {
      message = 'Cartão de crédito salvo com sucesso';
      acao = 'Cadastrar cartão de crédito';
    }

    const conta = await submitConta(contaCartao, pageable, message, acao);

    return conta;
  };

  const handleDelete = async (filtro) => {
    excluirAlert({
      confirmar: async () => {
        try {
          eventReactGA(CONTA, 'Excluir conta');
          await contaApi.delete({ ...filtro, ids: filtro.ids.map((c) => c.id) });

          let message;

          if (filtro?.ids?.length === 1) {
            const tipoConta = filtro.ids[0].tipo;

            if (
              tipoConta === 'CONTA_CORRENTE' ||
              tipoConta === 'CONTA_POUPANCA' ||
              tipoConta === 'CONTA_INVESTIMENTO'
            ) {
              message = 'Conta bancária excluída com sucesso';
            } else if (tipoConta === 'CONTA_CAIXINHA') {
              message = 'Conta caixinha excluída com sucesso';
            } else if (tipoConta === 'CONTA_CARTAO') {
              message = 'Cartão de crédito excluído com sucesso';
            }
          } else {
            message = 'Contas excluídas com sucesso';
          }

          showSuccess(message);
          setSelecao([]);
          fetchContas();
          fetchContasCorrente();
        } catch (err) {
          apiUtils.handleApiError(err);
        }
      },
    });
  };

  const handleSelecaoChange = (selecao) => {
    setSelecao(selecao);
  };

  const handleTipoSelecaoChange = (tipoSelecao) => {
    setTipoSelecao(tipoSelecao);
  };

  return (
    <>
      <ContaTable
        onSearch={fetchConta}
        onSelecaoChange={handleSelecaoChange}
        onTipoSelecaoChange={handleTipoSelecaoChange}
        onDelete={handleDelete}
        fetchContas={fetchContas}
        fetchConta={fetchConta}
      />
      <EditarContaBancariaModal onSubmit={handleSubmitContaBancaria} />
      <EditarContaCaixinhaModal onSubmit={handleSubmitContaCaixinha} />
      <EditarContaCartaoModal onSubmit={handleSubmitContaCartao} />
      <VisualizarContaBancariaModal />
      <VisualizarContaCaixinhaModal />
      <VisualizarContaCartaoModal />
    </>
  );
};

const ContaContainer = () => (
  <ContaProvider>
    <Conta />
  </ContaProvider>
);

export default ContaContainer;
